<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t('sidebar.masterplanStreets.dialog.title')"
    :loading="loading"
    dialog-width="76%"
    header-shadow
    agree-text="dialog.agreeSave"
    @agree="save"
  >
    <template #body>
      <v-col cols="12" class="pt-5">
        <v-row no-gutters align="center">
          <v-col cols="auto" style="width: 300px">
            <data-input
              v-model="newSituationType"
              :data-type="{ name: 'select' }"
              :items="Object.keys(typeAttributeDict)"
              :label="$i18n.t('sidebar.masterplanStreets.dialog.roadType')"
              hide-details
            />
          </v-col>
          <v-col cols="auto" class="pl-2">
            <base-rounded-button
              icon="mdi-plus"
              size="x-small"
              color="success"
              :disabled="!newSituationType"
              :tooltip="newSituationType ? $i18n.t('sidebar.masterplanStreets.dialog.addRoadType') : ''"
              @click="addItem(newSituationType)"
            />
          </v-col>
        </v-row>
        <v-row no-gutters class="pt-3">
          <draggable-list
            :value.sync="items"
            allow-elements-on-main-level
            disable-group-adding
            fill-width
            @change="onOrderUpdate"
          >
            <template #listElement="item">
              <v-row no-gutters class="flex-nowrap" align="center" justify="center" style="height: 60px">
                <v-col cols="auto" class="masterplan-street-color-badge" :style="`background-color: ${item.color}`" />
                <v-col cols="2" class="text-body-2 text-left pl-2">
                  {{ item.text }}
                </v-col>
                <v-col
                  v-for="(attribute, attributeIdx) of attributesMapped"
                  :key="attributeIdx"
                  class="text-body-2 pl-2"
                  :class="{ 'masterplan-street-input-divider': attribute.border }"
                  :cols="attribute.copy ? '3' : '2'"
                >
                  <template v-if="attribute.copy">
                    <v-row no-gutters justify="center" align="center">
                      <v-col cols="10">
                        <data-input
                          v-model="item.properties[attribute.name]"
                          v-bind="attribute"
                          @change="onAttributeChange(attribute)"
                        />
                      </v-col>
                      <v-col cols="2" class="pl-1">
                        <v-tooltip bottom color="font">
                          <template #activator="{ on }">
                            <v-icon
                              color="rgba(0,0,0,0.7)"
                              size="20"
                              v-on="on"
                              @click="onCopyToggle(item, attribute)"
                              :disabled="!item.properties[attribute.name] && item.properties[attribute.name] !== 0"
                            >
                              mdi-content-copy
                            </v-icon>
                          </template>
                          {{ $i18n.t('sidebar.masterplanStreets.dialog.copyValueToOthers') }}
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </template>
                  <template v-else>
                    <data-input
                      v-model="item.properties[attribute.name]"
                      v-bind="attribute"
                      @change="onAttributeChange(attribute)"
                    />
                  </template>
                </v-col>
                <v-col cols="2" class="text-body-2 pl-2">
                  <v-row no-gutters align="center" justify="center">
                    <v-col cols="8" class="mr-2">
                      <v-row no-gutters justify="center" class="masterplan-street-calculator-text">
                        {{ $i18n.t('sidebar.masterplanStreets.dialog.calculator') }}
                      </v-row>
                      <v-row no-gutters align="center" justify="center">
                        <v-chip
                          :disabled="!item.properties[lightingSituationAttributes.situation_attribute]"
                          color="primary"
                          label
                          small
                          :class="{
                            'white--text': !$_isHexColorLight(item.badgeColor),
                            'font--text': $_isHexColorLight(item.badgeColor),
                          }"
                          @click="onCalculateToggle(item)"
                        >
                          {{ $i18n.t('button.calculate') }}
                        </v-chip>
                        <v-chip
                          v-if="item.calculatorBadge"
                          :color="item.badgeColor"
                          label
                          small
                          class="ml-1"
                          :class="{
                            'white--text': !$_isHexColorLight(item.badgeColor),
                            'font--text': $_isHexColorLight(item.badgeColor),
                          }"
                        >
                          {{ item.calculatorBadge }}
                        </v-chip>
                      </v-row>
                    </v-col>
                    <v-col cols="2">
                      <v-icon color="error" @click="onDelete(item)">
                        mdi-close
                      </v-icon>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </template>
          </draggable-list>
        </v-row>
        <main-large-dialog
          v-if="isCopyLoaded"
          :is-visible.sync="isCopyVisible"
          :title="$i18n.t('sidebar.masterplanStreets.dialog.copyFrom', { value: currentCopyData.text })"
          agree-text="dialog.agreeCopy"
          header-shadow
          dialog-width="30%"
          @agree="copy"
        >
          <template #body>
            <v-col cols="12" class="pt-3">
              <v-row no-gutters>
                <v-col cols="12" class="text-left font-weight-bold" style="font-size: 16px">
                  {{ `${currentCopyData.attributeLabel}: ${currentCopyData.attributeValue}` }}
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-checkbox
                  :value="currentCopyData.otherItems?.find(element => element.checked)"
                  :on-icon="
                    currentCopyData.otherItems?.every(element => element.checked)
                      ? 'mdi-checkbox-marked'
                      : 'mdi-minus-box'
                  "
                  @change="toggleAllOtherCopyItems"
                  hide-details
                >
                  <template #label>
                    <div class="text-body-2 text-left" style="color: #202124">
                      {{ $i18n.t('dialog.selectAll') }}
                    </div>
                  </template>
                </v-checkbox>
              </v-row>
              <v-row no-gutters v-for="otherItem of currentCopyData.otherItems" :key="otherItem.value">
                <v-checkbox v-model="otherItem.checked" hide-details>
                  <template #label>
                    <div class="text-body-2 text-left" style="color: #202124">
                      {{ otherItem.text }}
                    </div>
                  </template>
                </v-checkbox>
              </v-row>
            </v-col>
          </template>
        </main-large-dialog>
        <main-large-dialog
          v-if="isCalculatorLoaded"
          :is-visible.sync="isCalculatorVisible"
          :is-valid="calculatorValid"
          :title="$i18n.t('sidebar.masterplanStreets.dialog.calculate', { value: currentCalculatorData.text })"
          agree-text="button.calculate"
          header-shadow
          dialog-width="25%"
          min-body-height="200px"
          @agree="calculate"
        >
          <template #body>
            <v-col cols="12" class="pt-3">
              <v-form v-model="calculatorValid" ref="calculator-form">
                <v-row no-gutters v-for="(attribute, idx) of currentCalculatorData.calculatorMetadata" :key="idx">
                  <v-col cols="12">
                    <data-input
                      v-model="
                        currentCalculatorData.calculator[currentCalculatorData.situation].params[attribute.label]
                      "
                      v-bind="attribute"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-col>
          </template>
        </main-large-dialog>
      </v-col>
    </template>
  </main-large-dialog>
</template>
<script>
import { call, get } from 'vuex-pathify';
import { sum } from 'lodash';

import DataInput from '@/components/DataInput';
import DraggableList from '@/components/DraggableList';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'MasterplanStreetsWidgetDialog',
  components: { DataInput, DraggableList, MainLargeDialog },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    fid: {
      type: Number,
      required: true,
    },
    features: {
      type: Array,
      default: () => {
        return [];
      },
    },
    featuresMappingFunction: {
      type: Function,
    },
    typeAttributeDict: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  computed: {
    dataSources: get('layers/metadata'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },

    calculatorMetadata: get('admin/settingsValues@masterplan_2_streets_situations_data'),
    calculatorMetadataSituations() {
      return Object.keys(this.calculatorMetadata);
    },
    masterplanSituationsDataSourceMapping: get(
      'admin/modulesMapping@masterplan_2_streets@masterplan_situation_datasource'
    ),
    lightingSituationDataSourceName() {
      return this.masterplanSituationsDataSourceMapping?.datasource || '';
    },
    lightingSituationDataSource() {
      return this.dataSources[this.lightingSituationDataSourceName] || undefined;
    },
    lightingSituationAttributes() {
      return this.masterplanSituationsDataSourceMapping?.settings || {};
    },
    lightingSituationAttributesMetadata() {
      return Object.entries(this.lightingSituationAttributes).reduce((total, [key, value]) => {
        const attribute = this.lightingSituationDataSource?.attributes_schema.attributes.find(
          attribute => attribute.name === value
        );
        const unit = attribute.measurement_unit?.symbol;
        total[key] = {
          dataType: { ...attribute.data_type, ...(attribute.allowed_values ? { name: 'select' } : {}) },
          ...(attribute.allowed_values ? { items: attribute.allowed_values } : {}),
          name: value,
          label: `${attribute.verbose_name}${unit ? ` [${unit}]` : ''}`,
          hideDetails: true,
          persistentPlaceholder: true,
        };
        return total;
      }, {});
    },
    attributesMapped() {
      return this.attributes.map(attribute => {
        return { ...attribute, ...this.lightingSituationAttributesMetadata[`${attribute.name}_attribute`] };
      });
    },
  },
  data: () => ({
    attributes: [
      {
        name: 'width',
      },
      {
        name: 'situation',
        copy: true,
      },
      {
        name: 'class',
        copy: true,
        border: true,
      },
    ],
    deletedFeaturesIds: [],
    loading: false,
    items: [],
    newSituationType: '',

    isCopyLoaded: false,
    isCopyVisible: false,
    currentCopyData: {},

    calculatorValid: false,
    currentCalculatorData: {},
    isCalculatorLoaded: false,
    isCalculatorVisible: false,
  }),
  methods: {
    editMultipleDataSourceFeatures: call('layers/editMultipleDataSourceFeatures'),

    addItem(type = this.newSituationType) {
      this.items = this.featuresMappingFunction([
        ...this.items,
        {
          properties: {
            [this.lightingSituationAttributes.type_attribute]: type,
            [this.lightingSituationAttributes.order_attribute]: this.items.length,
            [this.lightingSituationAttributes.street_id_attribute]: this.fid,
          },
        },
      ]);
    },

    onCopyToggle(item, attribute) {
      this.currentCopyData = {
        text: item.text,
        attributeName: attribute.name,
        attributeLabel: attribute.label,
        attributeValue: item.properties[attribute.name],
        otherItems: this.items
          .map((i, idx) => {
            return { value: idx, text: i.text, checked: false };
          })
          .filter((i, idx) => idx !== item.idx),
      };
      this.isCopyLoaded = true;
      this.isCopyVisible = true;
    },
    toggleAllOtherCopyItems(e) {
      this.currentCopyData.otherItems = this.currentCopyData.otherItems.map(item => {
        return { ...item, checked: e };
      });
    },
    copy() {
      this.currentCopyData.otherItems.forEach(item => {
        if (item.checked) {
          this.$set(
            this.items[item.value].properties,
            this.currentCopyData.attributeName,
            this.currentCopyData.attributeValue
          );
        }
      });
      this.items = this.featuresMappingFunction(this.items);
      this.isCopyVisible = false;
      this.currentCopyData = {};
    },

    onCalculateToggle(item) {
      const calculatorValue = item.properties[this.lightingSituationAttributes.calculation_attribute];
      const situationValue = item.properties[this.lightingSituationAttributes.situation_attribute];
      if (!calculatorValue) {
        this.setEmptyCalculator(item);
      } else {
        try {
          this.currentCalculatorData = {
            idx: item.idx,
            text: item.text,
            situation: situationValue,
            calculator: JSON.parse(item.properties[this.lightingSituationAttributes.calculation_attribute]),
            calculatorMetadata: this.getCurrentCalculatorMetadata(situationValue),
          };
        } catch {
          this.setEmptyCalculator(item);
        }
      }
      this.isCalculatorLoaded = true;
      this.isCalculatorVisible = true;
      this.$refs['calculator-form']?.resetValidation();
    },
    setEmptyCalculator(item) {
      const situationValue = item.properties[this.lightingSituationAttributes.situation_attribute];
      this.currentCalculatorData = {
        ...{ idx: item.idx, text: item.text, situation: situationValue },
        calculator: {
          ...this.calculatorMetadataSituations.reduce((total, current) => {
            total[current] = {
              class: null,
              params: {},
            };
            return total;
          }, {}),
        },
        calculatorMetadata: this.getCurrentCalculatorMetadata(
          item.properties[this.lightingSituationAttributes.situation_attribute]
        ),
      };
    },
    getCurrentCalculatorMetadata(value) {
      return Object.entries(this.calculatorMetadata[value]).map(([key, value]) => {
        const items = Object.entries(value).map(([k, v]) => {
          return { text: k, value: v };
        });
        return {
          label: key,
          dataType: { name: 'select' },
          items,
          persistentPlaceholder: true,
          rules: [v => !!v || v === 0 || 'rules.required'],
        };
      });
    },
    calculate() {
      const situationValue = this.currentCalculatorData.situation;
      const sumValue = sum(Object.values(this.currentCalculatorData.calculator[situationValue].params));
      let classValue = 6 - sumValue;
      if (situationValue === 'C' && classValue > 5) classValue = 5;
      else if (classValue > 6) classValue = 6;
      else if (classValue < 0) classValue = 0;

      const calculator = JSON.stringify({
        ...this.currentCalculatorData.calculator,
        [situationValue]: { ...this.currentCalculatorData.calculator[situationValue], class: classValue },
      });
      this.$set(
        this.items[this.currentCalculatorData.idx].properties,
        this.lightingSituationAttributes.calculation_attribute,
        calculator
      );
      this.items = this.featuresMappingFunction(this.items);
      this.isCalculatorVisible = false;
      this.currentCalculatorData = {};
    },

    onDelete(item) {
      this.items.splice(item.idx, 1);
      this.items = this.featuresMappingFunction(
        this.items.map((i, idx) => {
          return { ...i, properties: { ...i.properties, [this.lightingSituationAttributes.order_attribute]: idx } };
        })
      );
      if (item.id || item.id === 0) this.deletedFeaturesIds.push(item.id);
    },

    onOrderUpdate(e) {
      if (e?.moved) {
        this.items.forEach((item, idx) => {
          this.$set(item.properties, this.lightingSituationAttributes.order_attribute, idx);
        });
        this.items = this.featuresMappingFunction(this.items);
      }
    },
    onAttributeChange(attribute) {
      if (attribute.name === this.lightingSituationAttributes['situation_attribute']) {
        this.items = this.featuresMappingFunction(this.items);
      }
    },

    async save() {
      this.loading = true;
      try {
        const addedItems = this.items.filter(item => !item.id && item.id !== 0);
        const editedItems = this.items.filter(item => item.id || item.id === 0);
        const payload = {
          data_source_name: this.lightingSituationDataSourceName,
          delete: {
            features_ids: this.deletedFeaturesIds,
          },
          insert: addedItems.map(item => {
            return { id: null, ...item.properties };
          }),
          update: editedItems.map(item => {
            return { fid: item.id, properties: item.properties };
          }),
        };
        await this.editMultipleDataSourceFeatures(payload);
        this.computedIsVisible = false;
        this.$emit('onSave');
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    computedIsVisible: {
      handler(nV) {
        if (nV) {
          this.items = JSON.parse(JSON.stringify(this.features));
          this.deletedFeaturesIds = [];
          this.currentCopyData = {};
          this.currentCalculatorData = {};
          this.newSituationType = '';
        }
      },
      immediate: true,
    },
  },
};
</script>
<style lang="scss" scoped>
.masterplan-street-color-badge {
  height: 61px;
  width: 6px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.masterplan-street-input-divider {
  @include border(right);
}
.masterplan-street-calculator-text {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.6);
}
</style>
